/**
 * IONLOOP Assistant — runtime knowledge loader.
 *
 * Loads the build-time generated knowledge index (dist/assets/knowledge/
 * ionloop-knowledge.json), caches it in memory, and exposes predictable
 * read APIs for the Assistant UI and the local retrieval engine.
 *
 * All values from the index are treated as untrusted data and are never
 * rendered via innerHTML by this module.
 *
 * CommonJS exports so the same module can be required by Node test harnesses
 * and bundled by Webpack for the browser.
 */

const DEFAULT_INDEX = Object.freeze({
    version: 0,
    generatedAt: '',
    documents: []
});

let cachedIndex = null;
let inflightPromise = null;

/**
 * Resolve the knowledge index URL relative to the current page.
 * The static site is served at varying depths (/, /trading-tools/, ...) so we
 * compute the web root from the current pathname depth.
 */
function resolveKnowledgeUrl() {
    const pathname = window.location.pathname || '/';
    const segments = pathname.split('/').filter(Boolean);
    let webRoot = './';
    if (segments.length > 0) {
        // Clean URLs have no file extension, so depth is derived from whether
        // the pathname ends in a slash (a directory) or not (a page).
        const isDir = pathname.endsWith('/');
        const depth = isDir ? segments.length : segments.length - 1;
        if (depth > 0) {
            webRoot = '../'.repeat(depth);
        }
    }
    return webRoot + 'assets/knowledge/ionloop-knowledge.json';
}

/**
 * Basic structural sanity check. Rejects obviously malformed payloads without
 * throwing into the caller.
 */
function sanitizeIndex(data) {
    if (!data || typeof data !== 'object' || !Array.isArray(data.documents)) {
        return DEFAULT_INDEX;
    }
    return {
        version: typeof data.version === 'number' ? data.version : 1,
        generatedAt: typeof data.generatedAt === 'string' ? data.generatedAt : '',
        documents: data.documents
    };
}

/**
 * Load (and cache) the knowledge index.
 * @returns {Promise<Object>} resolves to { version, generatedAt, documents }
 */
function loadKnowledgeIndex() {
    if (cachedIndex) {
        return Promise.resolve(cachedIndex);
    }
    if (inflightPromise) {
        return inflightPromise;
    }

    inflightPromise = fetch(resolveKnowledgeUrl())
        .then(function (response) {
            if (!response.ok) {
                throw new Error('knowledge index HTTP ' + response.status);
            }
            return response.json();
        })
        .then(function (data) {
            cachedIndex = sanitizeIndex(data);
            inflightPromise = null;
            return cachedIndex;
        })
        .catch(function (err) {
            // Fail gracefully: assistant continues with an empty index.
            inflightPromise = null;
            if (window.console && console.warn) {
                console.warn('[IONLOOP Assistant] Knowledge index unavailable:', err);
            }
            cachedIndex = DEFAULT_INDEX;
            return cachedIndex;
        });

    return inflightPromise;
}

/**
 * @returns {Promise<Array>} all knowledge documents
 */
function getKnowledgeDocuments() {
    return loadKnowledgeIndex().then(function (index) {
        return index.documents;
    });
}

/**
 * @param {string} category one of the Assistant UI categories
 * @returns {Promise<Array>} documents in the given category
 */
function getKnowledgeByCategory(category) {
    return getKnowledgeDocuments().then(function (documents) {
        if (!category) {
            return documents;
        }
        return documents.filter(function (doc) {
            return doc.category === category;
        });
    });
}

/**
 * @returns {Promise<Object>} map of category → document count, derived from
 *                            the generated index (never hard-coded).
 */
function getKnowledgeCategoryCounts() {
    return getKnowledgeDocuments().then(function (documents) {
        const counts = {};
        documents.forEach(function (doc) {
            const category = doc.category || 'unknown';
            counts[category] = (counts[category] || 0) + 1;
        });
        return counts;
    });
}

const api = {
    loadKnowledgeIndex,
    getKnowledgeDocuments,
    getKnowledgeByCategory,
    getKnowledgeCategoryCounts
};

// Expose a global hook for pages/scripts that are not part of the module graph.
if (typeof window !== 'undefined') {
    window.IonloopKnowledge = api;
}

module.exports = api;
